import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { MANAGEMENT_TABLE_AREA_SX } from "components/management/managementTheme";
import { isClientRunExecuting } from "features/integrationsBeta/clientRunStatus";
import { useGetClientRunHistoryQuery } from "features/integrationsBeta/integrationsBeta";
import { useGetClientRunStatusesQuery } from "features/integrationsBeta/platformEnums";
import type { TimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";
import { useInFlightPolling } from "features/integrationsBeta/useInFlightPolling";
import { useEffect, useState } from "react";
import ClientHistoryFilterBar, {
  type ClientHistoryFilters,
} from "./ClientHistoryFilterBar";
import ClientRunDetailsDialog from "./ClientRunDetailsDialog";
import ClientRunHistoryTable from "./ClientRunHistoryTable";
import RunsPagination from "./RunsPagination";

const DEFAULT_PAGE_SIZE = 10;

type Props = {
  integrationId: number;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
  /** Internal users: the "what the client sees" caption + cross-links. */
  internalLinks: boolean;
  /** Selected run (from `?run=`); null when none. */
  selectedRunId: number | null;
  onSelectRun: (runId: number | null) => void;
  onOpenInternalRun: (runId: number) => void;
  clientStatusLabel: (value: string | null | undefined) => string;
};

/**
 * The "Client history" tab (CAL-5776): the paged, filterable run history in
 * the client's shape, refreshing while a run is executing, with a details
 * dialog per run. Self-contained: owns its page, size and filters.
 */
export default function ClientRunHistoryPanel({
  integrationId,
  timezoneMode,
  displayZone,
  internalLinks,
  selectedRunId,
  onSelectRun,
  onOpenInternalRun,
  clientStatusLabel,
}: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<ClientHistoryFilters>({});
  const { data: statuses } = useGetClientRunStatusesQuery();

  const [pollingInterval, setPollingActive] = useInFlightPolling();
  const { data, isLoading, isFetching } = useGetClientRunHistoryQuery(
    { integration_id: integrationId, page, page_size: pageSize, ...filters },
    { pollingInterval },
  );
  const rows = data?.results ?? [];
  const anyExecuting = rows.some(isClientRunExecuting);
  useEffect(
    () => setPollingActive(anyExecuting),
    [anyExecuting, setPollingActive],
  );
  // A background poll must not dim the rows every fifteen seconds.
  const busy = isFetching && pollingInterval === 0;
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <ClientHistoryFilterBar
          filters={filters}
          onChange={(next) => {
            setFilters(next);
            setPage(1);
          }}
          statuses={statuses ?? []}
        />
        {internalLinks && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <VisibilityOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Preview: this is exactly what the client sees.
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ ...MANAGEMENT_TABLE_AREA_SX, flexDirection: "column" }}>
        <ClientRunHistoryTable
          rows={rows}
          isLoading={isLoading}
          isFetching={busy}
          selectedRunId={selectedRunId}
          onSelect={(row) => onSelectRun(row.id)}
          clientStatusLabel={clientStatusLabel}
          timezoneMode={timezoneMode}
          displayZone={displayZone}
          onOpenInternalRun={internalLinks ? onOpenInternalRun : undefined}
          hasFilters={hasFilters}
          pagination={
            <RunsPagination
              page={page}
              pageSize={pageSize}
              total={data?.count ?? 0}
              disabled={busy}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        />
      </Box>

      <ClientRunDetailsDialog
        open={selectedRunId !== null}
        runId={selectedRunId}
        placeholder={rows.find((row) => row.id === selectedRunId) ?? null}
        onClose={() => onSelectRun(null)}
        displayZone={displayZone}
        clientStatusLabel={clientStatusLabel}
        onOpenInternalRun={
          internalLinks && selectedRunId !== null
            ? () => onOpenInternalRun(selectedRunId)
            : undefined
        }
      />
    </Box>
  );
}
